import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Lock, Play, Scissors, Star, CreditCard, Users } from "lucide-react";
import StripeCheckoutModal from "@/components/StripeCheckoutModal";
import { useDynamicPricing, whenReady } from "@/lib/subscriptionPricing";

const PaywallPage = () => {
  const { user, api } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [detail, setDetail] = useState(null);
  // Tier-aware pricing — an amateur (adult-league) member must see their own
  // plan here, not the youth one, and checkout must be handed the matching plan
  // id or the backend rejects it as the wrong tier. Amounts come from Stripe,
  // so no figure is written down here to go stale.
  const pricing = useDynamicPricing();

  useEffect(() => {
    if (!user) return;
    api().get("/subscription/status").then(r => setDetail(r.data)).catch(() => {});
  }, [api, user]);

  const thresholdCopy = (() => {
    if (!detail) return null;
    if (detail.reason === "org_size_threshold") {
      return {
        badge: `${detail.count} / ${detail.threshold} members`,
        headline: "Your org admin plan is almost free",
        sub: `Your organization unlocks free access once it reaches ${detail.threshold} approved members. You're ${detail.remaining} away.`,
      };
    }
    if (detail.reason === "team_size_threshold") {
      return {
        badge: `${detail.count} / ${detail.threshold} team members`,
        headline: "Almost there, coach",
        sub: `Your team unlocks free access once it reaches ${detail.threshold} approved members. You're ${detail.remaining} away.`,
      };
    }
    return null;
  })();

  return (
    <div data-testid="paywall-page" style={{
      padding: '60px 40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: 'calc(100vh - 60px)',
    }}>
      <div style={{ maxWidth: 480, textAlign: 'center' }}>
        <div style={{
          width: 88, height: 88, borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(255,107,0,0.15), rgba(0,229,255,0.1))',
          border: '2px solid rgba(255,107,0,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 28px',
        }}>
          <Lock size={36} style={{ color: '#ff6b00' }} />
        </div>

        <h1 data-testid="paywall-title" style={{
          fontFamily: "'Outfit', sans-serif", fontSize: 32, fontWeight: 800, marginBottom: 12,
          color: '#fff',
        }}>{thresholdCopy?.headline || "Unlock Full Access"}</h1>

        <p style={{ color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1.6, marginBottom: 24 }}>
          {/* Tier-neutral wording until the plan lands, so an amateur never reads
              "YSV Member" first and then sees it change to "YSV Amateur". */}
          {thresholdCopy?.sub || (pricing.ready
            ? `Subscribe to ${pricing.product_name} to watch games, create clips, and build highlight tapes.`
            : "Subscribe to watch games, create clips, and build highlight tapes.")}
        </p>

        {thresholdCopy && (
          <div data-testid="paywall-threshold-badge" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 14px', borderRadius: 20, marginBottom: 24,
            background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.3)',
            color: 'var(--cyan)', fontSize: 13, fontWeight: 600,
          }}>
            <Users size={14} /> {thresholdCopy.badge}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 32 }}>
          {[
            { icon: Play, label: "Watch Games", color: "#ff6b00" },
            { icon: Scissors, label: "Create Clips", color: "var(--cyan)" },
            { icon: Star, label: "Highlights", color: "#9c27b0" },
          ].map(f => (
            <div key={f.label} style={{ background: '#111114', border: '1px solid #1e1e24', borderRadius: 12, padding: '20px 12px', textAlign: 'center' }}>
              <f.icon size={24} style={{ color: f.color, marginBottom: 8 }} />
              <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{f.label}</p>
            </div>
          ))}
        </div>

        <div style={{ background: '#111114', border: '1px solid var(--cyan)', borderRadius: 16, padding: '28px 32px', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4, marginBottom: 8 }}>
            <span data-testid="paywall-price" style={{ fontSize: 48, fontWeight: 800, fontFamily: "'Outfit', sans-serif", color: 'var(--cyan)' }}>{whenReady(pricing, pricing.monthly_display)}</span>
            <span style={{ fontSize: 16, color: 'var(--text-secondary)' }}>/month</span>
          </div>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{whenReady(pricing, `${pricing.product_name} · Full Access`, "Full Access")}</p>
        </div>

        {/* Held until the plans call settles — clicking earlier would hand checkout
            the youth plan id, which the backend 400s for amateur accounts. */}
        <button data-testid="paywall-subscribe-btn" onClick={() => setShowModal(true)} disabled={!pricing.ready} style={{
          width: '100%', padding: '18px', background: '#ff6b00', border: 'none',
          borderRadius: 12, color: '#fff', fontSize: 18, fontWeight: 700,
          cursor: pricing.ready ? 'pointer' : 'wait', opacity: pricing.ready ? 1 : 0.7,
          fontFamily: "'Outfit', sans-serif", display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 10, transition: 'all 0.2s',
        }}>
          <CreditCard size={20} /> Subscribe Now
        </button>

        <p style={{ color: '#666', fontSize: 12, marginTop: 16 }}>
          Secure payment powered by Stripe. Cancel anytime.
        </p>

        {/* Only hand over a plan id we actually got from the backend; if the plans
            call failed, `pricing` is the youth guess, so let the modal re-resolve. */}
        {showModal && <StripeCheckoutModal planId={pricing.resolved ? pricing.monthly_plan_id : null} onClose={() => setShowModal(false)} />}
      </div>
    </div>
  );
};

export default PaywallPage;
